import { InvalidInputError } from '../errors/invalidInputError.js';
import { FormLogger } from '../form/formLogger.js';
import { message, Message } from '../message.js';

export class Token {
    expires: number;
    token: string;
}

export type ApiResponse = {
    data: any[];
    language: string;
    message: string;
    pagination: {
        items: number,
        limit: number,
        offset: number,
        pages: number,
        page: number
    },
    phpErrors: string[];
    statusCode: number;
    status: string;
    notification: Message;
}

export type WidgetValidation = {
    widget: string;
    errors: Array<Error>;
}

export const api = {
    baseUrl: (window.baseUrl || '') as string,
    lang: (window.lang || '') as string,
    log: false as boolean,
    showMessages: false as boolean,
    abortController: new AbortController(),

    getTokenStorageName(): string {
        return 'token';
    },

    showMessage(response: ApiResponse): void {
        if (this.showMessages === false || response.notification === null) {
            return;
        }

        message.log(response.notification.severity, response.notification.message);
    },

    checkTokenValid(token: any): boolean {
        if (typeof token !== 'object') {
            return false;
        }
        if (!('expires' in token) || !('token' in token)) {
            return false
        }
        if (token.expires < (+new Date() / 1000)) { // +new Date() gets current timestamp
            return false;
        }

        return true;
    },

    /**
     * Returns a Promise to get a valid token
     * @returns {Promise<Token>}
     */
    request_token(force = false): Promise<Token> {
        let token: Token = null;
        try {
            token = JSON.parse(localStorage.getItem(this.getTokenStorageName()));
            if (!this.checkTokenValid(token)) throw new Error('invalid token');
        } catch (e) {
            token = null;
        }

        return new Promise((resolve, reject) => {
            // resolve if already token available
            if (force === false && token !== null) {
                resolve(token);
                return;
            }

            // get new token
            fetch(`${this.baseUrl}api/auth/token`, {
                method: 'POST',
                body: null,
            })
                .then(response => response.json())
                .then((response: ApiResponse & {data: Token}) => {
                    const t = response.data;

                    if (!this.checkTokenValid(t)) {
                        throw new Error('received invalid token');
                    }

                    localStorage.setItem(this.getTokenStorageName(), JSON.stringify(t));
                    this.showMessage(response);
                    resolve(t);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * make an api request, supplying fitting headers
     * @param method
     * @param url
     * @param data
     * @param forceLang
     *
     */
    api_request(
        method: string,
        url: string,
        data: Record<string, unknown>,
        forceLang?: string,
    ): Promise<ApiResponse> {
        // make request
        return new Promise((resolve, reject) => {
            this.request_token().then((token: Token) => {
                window.apiTries = (window.apiTries || {});
                // define headers
                const headers = new Headers({
                    Authorization: `Bearer ${token.token}`,
                    'Content-Type': 'application/json',
                });
                const languageHeader = `${method.toUpperCase() !== 'GET' ? 'Content' : 'Accept'}-Language`;
                const language = (forceLang !== null && typeof forceLang !== 'undefined') ? forceLang : this.lang;

                // add content-language or accept-language header
                headers.append(languageHeader, language);

                // define object to send
                const fetchParams = {
                    body: ['GET', 'DELETE'].indexOf(method.toUpperCase()) === -1 ? JSON.stringify(data) : null,
                    method,
                    headers,
                    signal: this.abortController?.signal,
                };

                // execute fetch with supplied options
                fetch(url, fetchParams)
                    .then(r => FormLogger.logResponse(r, this.log), e => FormLogger.logError(e, method !== 'GET' && this.log))
                    .then((response: Response) => {
                        // catch 204 (no content)
                        if (response.status === 204) {
                            return {
                                statusCode: 204,
                                status: 'No Content',
                                notification: null,
                                data: [],
                            } as ApiResponse;
                        }

                        // check for json-ness
                        const contentType = response.headers.get('Content-Type');
                        if (contentType && contentType.indexOf('application/json') === -1) {
                            response.text().then(text => {
                                // check 500
                                if (response.status === 500) {
                                    // log exception (in case of slim exception)
                                    const htmlParser = new DOMParser();
                                    const html = htmlParser.parseFromString(text, 'text/html');
                                    throw new Error(html.querySelectorAll('div')[2].innerHTML);
                                }

                                return text;
                            });
                        } else {
                            return response.json();
                        }

                        return null;
                    })
                    .then((response: ApiResponse) => {
                        // catch response undefined
                        if (typeof response === 'undefined' || response === null) {
                            throw new Error('Oops! An error occured.');
                        }

                        this.showMessage(response);

                        // catch 5xx errors
                        if (response.statusCode.toString().search(/5\d\d/) === 0) {
                            throw new Error(`${response.statusCode.toString()}: ${response.message}`);
                        }

                        // catch widget validation errors
                        if (response.statusCode === 400 && response.message === 'invalid input') {
                            console.log(response.data, response.message);
                            throw new InvalidInputError(response.data, response.message as any);
                        }

                        // catch permission errors (try a few times)
                        if (response.statusCode === 401 && (window.apiTries[url] = (window.apiTries[url] || 0) + 1) < 5) {
                            console.log('retrying with fresh token. tries:', window.apiTries[url]);
                            // re-try with new token
                            localStorage.removeItem(this.getTokenStorageName());
                            resolve(this.api_request(method, url, data, forceLang));
                        }

                        // catch any non-2xx status codes
                        if (response.statusCode.toString().search(/2\d\d/) === -1) {
                            throw new Error(response.message);
                        }

                        // reset url tries counter
                        window.apiTries[url] = 0;

                        // resolve with ApiResponse
                        resolve(response);
                    })
                    .catch((error: Error) => {
                        if (error instanceof InvalidInputError) {
                            reject(error);
                        } else if (error instanceof DOMException) {
                            if (error.name === 'AbortError') resolve({} as ApiResponse);
                            reject(error);
                        } else {
                            error.message = `api_request failed: ${error.message}`;
                            reject(error);
                        }
                    });
            }, (error: Error) => {
                throw new Error(`request_token failed: ${error.message}`);
            }).catch((error: Error) => {
                reject(error);
            });
        });
    },

    /**
     * make a GET request with automatically supplied token
     * @param url
     * @param lang
     */
    api_get(url: string, lang: string = null): Promise<ApiResponse> {
        // make request
        return this.api_request('GET', url, null, lang);
    },

    /**
     * make a POST request with automatically supplied token
     * @param url
     * @param data
     */
    api_post(url: string, data: Record<string, unknown>): Promise<ApiResponse> {
        // make request
        return this.api_request('POST', url, data, null);
    },

    /**
     * make a PUT request with automatically supplied token
     * @param url
     * @param data
     * @param forceLang
     * @returns {*|Promise<T|never>}
     */
    api_put(url: string, data: Record<string, unknown>, forceLang?: string): Promise<ApiResponse> {
        // make request
        return this.api_request('PUT', url, data, forceLang);
    },

    /**
     * make a PATCH request with automatically supplied token
     * @param url
     * @param data
     * @param forceLang
     * @returns {*|Promise<T|never>}
     */
    api_patch(url: string, data: Record<string, unknown>, forceLang?: string): Promise<ApiResponse> {
        // make request
        return this.api_request('PATCH', url, data, forceLang);
    },

    /**
     * make a DELETE request with automatically supplied token
     * @param url
     * @returns {*|Promise<T|never>}
     */
    api_delete(url: string): Promise<ApiResponse> {
        // make request
        return this.api_request('DELETE', url, null, null);
    },

    /**
     * aborts the current request if supported
     */
    abort_request(): void {
        if (typeof this.abortController.abort !== 'undefined') {
            this.abortController.abort();
            this.abortController = new AbortController();
        }
    },

};

declare global {
    interface Window {
        apiTries: Record<string, number>;
        baseUrl: string,
        lang: string,
    }
}
